import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useParams, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { nextStep, prevStep, updateFormData } from '../Store/features/Units/unitsSlice';
import { addUnit, deleteAttachments, deletePictures, deleteSketch, editUnit, getUnitsDetials } from '../network/apis';
import LoadingOverlay from '../components/loading-overlay/LoadingOverlay';

const FormContext = createContext();

export const useUnitFormContext = () => useContext(FormContext);

export const FormProvider = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const isClone = location.state?.isClone;
  const fromPage = location.state?.from;
  const [detailsData, setDetails] = useState(
    location.state?.editData ? { data: location.state.editData } : []
  );
  const [uploadedFilesUploaded, setUploadedFilesUploaded] = useState({
    unitSketch: [],
    primaryPhoto: [],
    virtualTour: [],
    attachments: [],
    unitImages: [],
  });
  const [refetch, setRefetch] = useState(false);
  const currentPath = location.pathname;
  const isEditOrViewMode = currentPath.includes('/edit/') || currentPath.includes('/view/');
  const viewMode = currentPath.includes('/view/') || currentPath.includes('view');

  const fetchUnitDetails = async () => {
    try {
      const { data } = await getUnitsDetials(id);
      setDetails(data);
      const unitObj = data?.data || (data?.id ? data : null);
      if (unitObj) {
        dispatch(updateFormData(unitObj));
      }
    } catch (error) {
      console.error('Failed to fetch unit details:', error);
      toast.error('Failed to fetch unit details');
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.editData) {
      dispatch(updateFormData(location.state.editData));
    }
  }, []);

  useEffect(() => {
    if (id && isEditOrViewMode && fromPage !== 'edit-project-to-add-unit') {
      setSubmitLoading(true);
      fetchUnitDetails();
    } else {
      setSubmitLoading(false);
    }
  }, [id, refetch, currentPath]);

  const handleDelete = async (fileId, fieldName) => {
    let response;
    if (fieldName === 'attachment') {
      response = await deleteAttachments(fileId);
    } else if (fieldName === 'sketch') {
      response = await deleteSketch(fileId);
    } else {
      response = await deletePictures(fileId);
    }
    // Call the deletePictures API

    if (response?.data?.STATUS === '1') {
      toast.success(response?.data?.MESSAGE);
    } else {
      toast.error(response?.data?.MESSAGE);
    }
  };

  const [loading_submit, setSubmitLoading] = useState(true);
  const currentStep = useSelector((state) => state.form.currentStep);

  const [setCurrentStep] = useState(currentStep);
  const formDetails = useSelector((state) => state.unitform.formData);
  const isEditMode = currentPath.includes('/edit/');
  const { requestId, userId, managerId, isManager } = location.state || '';

  // Add Item FunctionCopy code
  function addItem(fieldName, items, acceptMultiple = true) {
    // Initialize the field if it doesn't exist
    if (!uploadedFilesUploaded[fieldName]) {
      uploadedFilesUploaded[fieldName] = [];
    }

    if (acceptMultiple) {
      // If multiple items are allowed, add items if they don't exist
      items.forEach((item) => {
        if (!uploadedFilesUploaded[fieldName].includes(item)) {
          uploadedFilesUploaded[fieldName].push(item);
        }
      });
    } else {
      // If multiple items are not allowed, replace the existing item
      uploadedFilesUploaded[fieldName] = [items[0]];
    }

    return uploadedFilesUploaded;
  }

  // Delete Item Function
  function deleteItem(fieldName, item) {
    if (uploadedFilesUploaded[fieldName]) {
      setUploadedFilesUploaded((prevUploadedFilesUploaded) => {
        const newUploadedFilesUploaded = { ...prevUploadedFilesUploaded };
        newUploadedFilesUploaded[fieldName] = newUploadedFilesUploaded[fieldName].filter((i) => i?.id !== item);
        return newUploadedFilesUploaded;
      });
    }
    return uploadedFilesUploaded;
  }

  const handleSubmit = (valuesFromForm) => {
    // Merge values from form with Redux store state to prevent stale state issues on immediate submission
    const mergedDetails = {
      ...formDetails,
      ...(valuesFromForm && typeof valuesFromForm === 'object' && !valuesFromForm.nativeEvent ? valuesFromForm : {}),
    };

    const addedUnit = {
      nameEn: mergedDetails?.nameEn,
      nameAr: mergedDetails?.nameAr,
      type: mergedDetails?.type,
      area: mergedDetails?.area,
      facade: mergedDetails?.facade || '',
      northernBoardEn: mergedDetails?.northernBoardEn || '',
      northernBoardAr: mergedDetails?.northernBoardAr || '',
      southernBoardEn: mergedDetails?.southernBoardEn || '',
      southernBoardAr: mergedDetails?.southernBoardAr || '',
      westernBoardEn: mergedDetails?.westernBoardEn || '',
      westernBoardAr: mergedDetails?.westernBoardAr || '',
      easternBoardEn: mergedDetails?.easternBoardEn || '',
      easternBoardAr: mergedDetails?.easternBoardAr || '',
      purpose: mergedDetails?.purpose,
      listingType: mergedDetails?.listingType,
      rentPrice: mergedDetails?.rentPrice || '',
      insurancePrice: mergedDetails?.insurancePrice || '',
      LISTING_TYPE: mergedDetails?.listingType || '',
      // bathrooms: mergedDetails?.bathrooms,
      // parking: mergedDetails?.parking,
      states: mergedDetails?.states,
      piecePrice: mergedDetails?.piecePrice,
      realEstateTax: mergedDetails?.realEstateTax,
      commissionFees: mergedDetails?.commissionFees,
      commissionFeesVat: mergedDetails?.commissionFeesVat,
      ownershipFees: mergedDetails?.ownershipFees,
      meterPrice: mergedDetails?.meterPrice,
      totalPrice: mergedDetails?.totalPrice,
      whatsappNumber: mergedDetails?.whatsappNumber,
      email: mergedDetails?.email,
      mobile: mergedDetails?.mobile,
      pieceDataStatus: mergedDetails?.pieceDataStatus,
      pieceStatus: mergedDetails?.pieceStatus,
      reservationStatus: mergedDetails?.reservationStatus,
      blockNumber: mergedDetails?.blockNumber,
      projectId: mergedDetails?.projectDetails?.id,
      totalPriceNoVat: mergedDetails?.totalPriceNoVat,
      levelNo: mergedDetails?.levelNo,
      buildingId: mergedDetails?.buildingId,
      roomDetails: mergedDetails?.roomDetails,
      virtualType: mergedDetails.virtualType ? 'IMAGE' : 'LINK',
      virtualLink: mergedDetails?.virtualLink || '',
      unitSketch: uploadedFilesUploaded?.unitSketch?.map((item) => item.id),
      primaryPhoto: uploadedFilesUploaded?.primaryPhoto?.map((item) => item.id),
      virtualTour: uploadedFilesUploaded?.virtualTour?.map((item) => item.id),
      attachments: uploadedFilesUploaded?.attachments?.map((item) => item.id),
      pictures: uploadedFilesUploaded?.unitImages?.map((item) => item.id),
      isClone: isClone ? 1 : 0,
    };
    setSubmitLoading(true);

    if (isEditMode) {
      editUnit({ ...addedUnit, id, requestId: requestId || '' })
        .then((response) => {
          setSubmitLoading(false);
          if (response.data.STATUS === 'SUCCESS') {
            toast.success(response.data.MESSAGE);
            if (fromPage === 'review-products') {
              navigate('/dashboard/review-products');
            } else {
              navigate(`/dashboard/units`);
            }
          } else if (response.data.STATUS === 'FAILED') {
            toast.error(response.data.MESSAGE);
          }
        })
        .catch((error) => {
          setSubmitLoading(false);
          toast.error(error.data.MESSAGE);
        });
    } else {
      addUnit(addedUnit)
        .then((response) => {
          setSubmitLoading(false);
          if (response.data.STATUS === 'SUCCESS') {
            toast.success(response.data.MESSAGE);
            if (fromPage === 'review-products') {
              navigate('/dashboard/review-products');
            } else {
              navigate(id ? `/dashboard/project/edit/${id}` : `/dashboard/units`, {
                state: id ? { from: 'add-unit-with-project', projectId: id } : { from: 'add-unit-without-project' },
              });
            }
          } else if (response.data.STATUS === 'FAILED') {
            toast.error(response.data.MESSAGE);
          }
        })
        .catch((error) => {
          setSubmitLoading(false);
          if (error?.response) toast.error(error?.response?.data?.message);
          else {
            toast.error(t('General.network_error'));
          }
        });
    }
  };

  const handleNext = () => {
    dispatch(nextStep());
  };

  const handlePrev = () => {
    dispatch(prevStep());
  };
  return (
    <FormContext.Provider
      value={{
        formDetails,
        handleDelete,
        handleSubmit,

        handleNext,
        handlePrev,
        addItem,
        deleteItem,
        currentStep,
        setCurrentStep,
        isEditMode,
        id,
        loading_submit,
        viewMode,
        userId,
        managerId,
        isManager,
        requestId,
        setDetails,
        detailsData,
        setSubmitLoading,
        refetch,
        setRefetch,
        uploadedFilesUploaded,
      }}
    >
      {loading_submit ? <p>{<LoadingOverlay />}</p> : children}
    </FormContext.Provider>
  );
};
